
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Review } from '@/lib/types';
import CompanyDisplay from './CompanyDisplay';
import ReviewDetails from './ReviewDetails';
import ReviewMetadata from './ReviewMetadata';
import ReviewContent from './ReviewContent';
import PerksPreview from './PerksPreview';
import PayToChatButton from './PayToChatButton';

interface ReviewCardProps {
  review: Review;
  className?: string;
  hideCompany?: boolean;
  hideTimeAgo?: boolean;
  layout?: 'vertical' | 'horizontal';
  disableCompanyLink?: boolean;
}

const ReviewCard = ({ 
  review, 
  className, 
  hideCompany = false,
  hideTimeAgo = false,
  layout = 'vertical',
  disableCompanyLink = false
}: ReviewCardProps) => {
  // Format date as "Month, Year"
  const formattedDate = new Date(review.createdAt).toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });
  const encodedCompanyName = encodeURIComponent(review.company);
  
  // Check if job title, reason for exit, and biggest problem have content
  const hasJobTitle = review.jobTitle && review.jobTitle.trim() !== '';
  const hasReasonForExit = review.reasonForExit && review.reasonForExit.trim() !== '';
  const hasBiggestProblem = review.biggestProblem && review.biggestProblem.trim() !== '';
  const hasPerks = review.perkRatings && review.perkRatings.length > 0;
  
  if (layout === 'horizontal') {
    return (
      <Card className={cn(
        "overflow-hidden border border-redflags-light transition-all duration-300 hover:shadow-md", 
        className
      )}>
        <CardContent className="p-6">
          {/* Company logo and name with experience indicator aligned on the right */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex flex-col gap-1">
              <CompanyDisplay 
                company={review.company}
                logo={review.logo} 
                disableCompanyLink={disableCompanyLink}
              />
              {!hideTimeAgo && <div className="text-sm text-redflags-gray">{formattedDate}</div>}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-end gap-2">
                <ReviewMetadata.ExperienceIndicator positiveExperience={review.positiveExperience} />
                {review.unique_ref && (
                  <PayToChatButton 
                    reviewId={review.id} 
                    reviewRef={review.unique_ref} 
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Job details (title, location, etc.) */}
          <ReviewMetadata 
            review={review}
            hasJobTitle={hasJobTitle}
          />
          
          {/* Review content sections */}
          <ReviewContent 
            reasonForExit={hasReasonForExit ? review.reasonForExit : undefined}
            biggestProblem={hasBiggestProblem ? review.biggestProblem : undefined}
          />
          
          {/* Preview of perks */}
          {hasPerks && (
            <PerksPreview count={review.perkRatings!.length} />
          )}
        </CardContent>
      </Card>
    );
  }
  
  // Vertical layout
  return (
    <Card className={cn(
      "overflow-hidden border border-redflags-light transition-all duration-300 hover:shadow-md", 
      className
    )}>
      {!hideCompany && (
        <CardHeader className="p-6 pb-2">
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <CompanyDisplay 
                company={review.company}
                logo={review.logo} 
                disableCompanyLink={disableCompanyLink}
              />
              {!hideTimeAgo && <div className="text-sm text-redflags-gray">{formattedDate}</div>}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-end gap-2">
                <ReviewMetadata.ExperienceIndicator positiveExperience={review.positiveExperience} />
                {review.unique_ref && (
                  <PayToChatButton 
                    reviewId={review.id} 
                    reviewRef={review.unique_ref} 
                  />
                )}
              </div>
            </div>
          </div>
        </CardHeader>
      )}
      
      <CardContent className="p-6 space-y-4">
        <ReviewDetails 
          jobTitle={hasJobTitle ? review.jobTitle : undefined}
          location={review.location}
          salary={review.salary}
        />
        
        <ReviewContent.Vertical 
          reasonForExit={hasReasonForExit ? review.reasonForExit : undefined}
          biggestProblem={hasBiggestProblem ? review.biggestProblem : undefined}
          perkRatings={hasPerks ? review.perkRatings : undefined}
        />
      </CardContent>
      
      <CardFooter className="px-6 py-4 bg-redflags-light/30 flex flex-col justify-center items-center">
        {!hideCompany && (
          <Button 
            asChild
            variant="outline"
            className="w-full bg-white hover:bg-redflags-light/50 text-redflags-red hover:text-redflags-red border-redflags-red/30 hover:border-redflags-red transition-all"
          >
            <Link to={`/company/${encodedCompanyName}`}>
              View company
              <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

// Helper to format exit date safely, ensuring it's a valid Date object
export const formatExitDate = (exitDate: Date | undefined): string => {
  if (!exitDate) return '';
  
  // Ensure exitDate is a proper Date object
  const dateObj = exitDate instanceof Date ? exitDate : new Date(exitDate);
  
  // Check if the date is valid before formatting
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('en-US', { 
    month: 'short', 
    year: 'numeric' 
  });
};

export default ReviewCard;
